// src/components/SkeletonLoaders.jsx
import React from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

/**
 * Skeleton loader for table data
 * @param {number} rows - Number of skeleton rows to display
 * @param {number} columns - Number of skeleton columns to display
 */
export const TableSkeleton = ({ rows = 5, columns = 4 }) => (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          {[...Array(columns)].map((_, idx) => (
            <TableCell key={idx}>
              <Skeleton variant="text" width="80%" />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {[...Array(rows)].map((_, rowIdx) => (
          <TableRow key={rowIdx}>
            {[...Array(columns)].map((_, colIdx) => (
              <TableCell key={colIdx}>
                <Skeleton variant="text" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

/**
 * Skeleton loader for card-based content
 * @param {number} count - Number of skeleton cards to display
 */
export const CardSkeleton = ({ count = 3 }) => (
  <Grid container spacing={3}>
    {[...Array(count)].map((_, idx) => (
      <Grid item xs={12} sm={6} md={4} key={idx}>
        <Paper sx={{ p: 2 }}>
          <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" width="100%" height={118} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="90%" />
          <Skeleton variant="text" width="40%" />
        </Paper>
      </Grid>
    ))}
  </Grid>
);

/**
 * Skeleton loader for list items
 * @param {number} count - Number of skeleton list items
 */
export const ListSkeleton = ({ count = 5 }) => (
  <Box>
    {[...Array(count)].map((_, idx) => (
      <Paper key={idx} sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="40%" height={24} />
          <Skeleton variant="text" width="80%" height={20} />
        </Box>
        <Skeleton variant="rectangular" width={80} height={36} />
      </Paper>
    ))}
  </Box>
);

/**
 * Skeleton loader for form inputs
 * @param {number} fields - Number of form fields
 */
export const FormSkeleton = ({ fields = 5 }) => (
  <Box>
    {[...Array(fields)].map((_, idx) => (
      <Box key={idx} sx={{ mb: 3 }}>
        <Skeleton variant="text" width="30%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={56} />
      </Box>
    ))}
    <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
      <Skeleton variant="rectangular" width={120} height={42} />
      <Skeleton variant="rectangular" width={120} height={42} />
    </Box>
  </Box>
);

/**
 * Skeleton loader for dashboard statistics
 */
export const StatsSkeleton = () => (
  <Grid container spacing={3}>
    {[...Array(4)].map((_, idx) => (
      <Grid item xs={12} sm={6} md={3} key={idx}>
        <Paper
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Skeleton variant="circular" width={60} height={60} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="80%" height={32} />
          <Skeleton variant="text" width="60%" height={24} />
        </Paper>
      </Grid>
    ))}
  </Grid>
);

/**
 * Skeleton loader for detailed view pages
 */
export const DetailViewSkeleton = () => (
  <Box>
    <Skeleton variant="text" width="60%" height={48} sx={{ mb: 3 }} />
    <Paper sx={{ p: 3, mb: 3 }}>
      <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {[...Array(6)].map((_, idx) => (
          <Grid item xs={12} sm={6} key={idx}>
            <Skeleton variant="text" width="30%" height={20} />
            <Skeleton variant="text" width="70%" height={24} sx={{ mb: 2 }} />
          </Grid>
        ))}
      </Grid>
    </Paper>
    <Paper sx={{ p: 3 }}>
      <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" width="100%" height={200} />
    </Paper>
  </Box>
);

/**
 * Skeleton loader for notification list
 */
export const NotificationSkeleton = ({ count = 5 }) => (
  <Box>
    {[...Array(count)].map((_, idx) => (
      <Paper
        key={idx}
        sx={{
          p: 2,
          mb: 1,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2
        }}
      >
        <Skeleton variant="circular" width={48} height={48} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Skeleton variant="text" width="40%" height={24} />
            <Skeleton variant="rounded" width={60} height={20} />
          </Box>
          <Skeleton variant="text" width="90%" height={20} />
          <Skeleton variant="text" width="70%" height={20} />
          <Skeleton variant="text" width="30%" height={16} sx={{ mt: 1 }} />
        </Box>
        <Skeleton variant="rectangular" width={24} height={24} />
      </Paper>
    ))}
  </Box>
);

/**
 * Skeleton loader for device grid
 */
export const DeviceGridSkeleton = ({ count = 6 }) => (
  <Grid container spacing={2}>
    {[...Array(count)].map((_, idx) => (
      <Grid item xs={12} sm={6} md={4} key={idx}>
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Skeleton variant="text" width="60%" height={28} />
            <Skeleton variant="circular" width={12} height={12} />
          </Box>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="rectangular" width="100%" height={80} sx={{ mt: 1 }} />
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Skeleton variant="rectangular" width="48%" height={32} />
            <Skeleton variant="rectangular" width="48%" height={32} />
          </Box>
        </Paper>
      </Grid>
    ))}
  </Grid>
);

/**
 * Generic content skeleton with customizable layout
 */
export const ContentSkeleton = () => (
  <Box>
    <Skeleton variant="text" width="50%" height={40} sx={{ mb: 3 }} />
    <Paper sx={{ p: 3, mb: 3 }}>
      <Skeleton variant="rectangular" width="100%" height={200} sx={{ mb: 2 }} />
      <Skeleton variant="text" width="100%" />
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="80%" />
    </Paper>
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" width="100%" height={150} />
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" width="100%" height={150} />
        </Paper>
      </Grid>
    </Grid>
  </Box>
);

export default {
  TableSkeleton,
  CardSkeleton,
  ListSkeleton,
  FormSkeleton,
  StatsSkeleton,
  DetailViewSkeleton,
  NotificationSkeleton,
  DeviceGridSkeleton,
  ContentSkeleton
};
